import { Link, useNavigate } from 'react-router-dom'
import { Upload, LogOut, User, Menu, X } from 'lucide-react'
import { useState } from 'react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { ROUTES, buildRoute } from '@/constants'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { SearchBar } from '@/components/SearchBar'
import { getInitials } from '@/utils'
import { useCurrentProfile } from '@/features/profile/hooks/useCurrentProfile'
import { cn } from '@/utils'

export function Navbar() {
  const { isAuthenticated, signOut } = useAuth()
  const { profile } = useCurrentProfile()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleSearch = (query: string) => {
    navigate(buildRoute.search(query))
    setMobileOpen(false)
  }

  const handleSignOut = async () => {
    await signOut()
    navigate(ROUTES.HOME)
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
      <div className="container mx-auto flex h-16 max-w-7xl items-center gap-4 px-4">

        {/* Logo */}
        <Link
          to={ROUTES.HOME}
          className="flex items-center gap-2 font-semibold tracking-tight"
          aria-label="UnsplashKH home"
        >
          {/* add image from public folder */}
          <img src="images/logo/logo.png" alt="UnsplashKH Logo" className="h-10 w-40" />
        </Link>

        {/* Desktop search */}
        <div className="hidden flex-1 max-w-lg md:flex">
          <SearchBar onSearch={handleSearch} />
        </div>

        {/* Desktop nav */}
        <nav className="ml-auto hidden items-center gap-1 md:flex" aria-label="Main navigation">
          <Button variant="ghost" size="sm" asChild>
            <Link to={ROUTES.EXPLORE}>Explore</Link>
          </Button>

          {isAuthenticated ? (
            <>
              <Button variant="ghost" size="sm" asChild>
                <Link to={ROUTES.UPLOAD}>
                  <Upload className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Upload
                </Link>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="ml-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Open user menu"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={profile?.avatar_url ?? undefined}
                        alt={profile?.username}
                      />
                      <AvatarFallback>
                        {getInitials(profile?.full_name ?? profile?.username ?? null)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-48">
                  {profile && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to={buildRoute.profile(profile.username)}>
                          <User className="mr-2 h-4 w-4" aria-hidden="true" />
                          Your profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={ROUTES.SETTINGS}>Settings</Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild>
                <Link to={ROUTES.LOGIN}>Sign in</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to={ROUTES.REGISTER}>Get started</Link>
              </Button>
            </>
          )}
        </nav>

        {/* Mobile menu toggle */}
        <button
          className="ml-auto rounded-md p-2 md:hidden"
          onClick={() => setMobileOpen((o) => !o)}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen
            ? <X className="h-5 w-5" aria-hidden="true" />
            : <Menu className="h-5 w-5" aria-hidden="true" />
          }
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={cn(
          'border-t md:hidden',
          mobileOpen ? 'block' : 'hidden',
        )}
        aria-label="Mobile navigation"
      >
        <div className="container mx-auto flex flex-col gap-3 px-4 py-4">
          <SearchBar onSearch={handleSearch} autoFocus={mobileOpen} />

          <nav className="flex flex-col gap-1">
            <Button variant="ghost" className="justify-start" asChild>
              <Link to={ROUTES.EXPLORE} onClick={() => setMobileOpen(false)}>
                Explore
              </Link>
            </Button>

            {isAuthenticated ? (
              <>
                <Button variant="ghost" className="justify-start" asChild>
                  <Link to={ROUTES.UPLOAD} onClick={() => setMobileOpen(false)}>
                    <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                    Upload photo
                  </Link>
                </Button>
                {profile && (
                  <Button variant="ghost" className="justify-start" asChild>
                    <Link
                      to={buildRoute.profile(profile.username)}
                      onClick={() => setMobileOpen(false)}
                    >
                      <User className="mr-2 h-4 w-4" aria-hidden="true" />
                      Your profile
                    </Link>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="justify-start text-destructive"
                  onClick={handleSignOut}
                >
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  Sign out
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" className="justify-start" asChild>
                  <Link to={ROUTES.LOGIN} onClick={() => setMobileOpen(false)}>Sign in</Link>
                </Button>
                <Button className="justify-start" asChild>
                  <Link to={ROUTES.REGISTER} onClick={() => setMobileOpen(false)}>Get started</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
      </div>
    </header>
  )
}
